// WebRTC Service - Handles peer-to-peer video/audio calls using Simple Peer
import SimplePeer from 'simple-peer';
import { chatService } from './chatService';

class WebRTCService {
  constructor() {
    this.peer = null;
    this.localStream = null;
    this.remoteStream = null;
    this.callId = null;
    this.conversationId = null;
    this.remoteUserId = null;
    this.isInitiator = false;
    this.callbacks = {};
  }

  // Initialize media stream (camera/microphone)
  async getMediaStream(videoEnabled = true, audioEnabled = true) {
    try {
      const constraints = {
        video: videoEnabled ? {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        } : false,
        audio: audioEnabled ? {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } : false
      };

      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      return this.localStream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      throw error;
    }
  }

  // Stop local media stream
  stopLocalStream() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
  }

  // Set callbacks for call events
  setCallbacks(callbacks) {
    this.callbacks = callbacks;
  }

  // Initiate a call
  async initiateCall(conversationId, remoteUserId, callType = 'video') {
    try {
      // Get media stream
      const videoEnabled = callType === 'video';
      await this.getMediaStream(videoEnabled, true);

      // Initiate call on server
      const response = await chatService.initiateCall(conversationId, callType);
      this.callId = response.call_id;
      this.conversationId = conversationId;
      this.remoteUserId = remoteUserId;
      this.isInitiator = true;

      // Create peer as initiator
      this.createPeer(true);

      return {
        callId: this.callId,
        localStream: this.localStream
      };
    } catch (error) {
      console.error('Error initiating call:', error);
      this.cleanup();
      throw error;
    }
  }

  // Answer an incoming call
  async answerCall(callId, conversationId, remoteUserId, callType = 'video') {
    try {
      this.callId = callId;
      this.conversationId = conversationId;
      this.remoteUserId = remoteUserId;
      this.isInitiator = false;

      // Get media stream
      const videoEnabled = callType === 'video';
      await this.getMediaStream(videoEnabled, true);

      // Notify server that call is answered
      await chatService.sendSignal(callId, 'call-answered', {}, remoteUserId);

      return {
        localStream: this.localStream
      };
    } catch (error) {
      console.error('Error answering call:', error);
      this.cleanup();
      throw error;
    }
  }

  // Create Simple Peer instance
  createPeer(initiator) {
    const stunUrls = (process.env.REACT_APP_STUN_URLS || 'stun:stun.l.google.com:19302')
      .split(',')
      .map(url => url.trim())
      .filter(Boolean);
    const iceServers = stunUrls.map(urls => ({ urls }));
    const turnUrl = (process.env.REACT_APP_TURN_URL || '').trim();
    const turnUsername = process.env.REACT_APP_TURN_USERNAME || '';
    const turnCredential = process.env.REACT_APP_TURN_CREDENTIAL || '';
    if (turnUrl && turnUsername && turnCredential) {
      iceServers.push({
        urls: turnUrl,
        username: turnUsername,
        credential: turnCredential,
      });
    }
    const config = {
      initiator,
      trickle: true,
      stream: this.localStream,
      config: {
        iceServers
      }
    };

    this.peer = new SimplePeer(config);

    // Handle signaling data
    this.peer.on('signal', async (data) => {
      const signalType = data.type === 'offer' ? 'offer' : 
                         data.type === 'answer' ? 'answer' : 'ice-candidate';
      
      try {
        await chatService.sendSignal(
          this.callId,
          signalType,
          data,
          this.remoteUserId
        );
      } catch (error) {
        console.error('Error sending signal:', error);
      }
    });

    // Handle remote stream
    this.peer.on('stream', (stream) => {
      this.remoteStream = stream;
      if (this.callbacks.onRemoteStream) {
        this.callbacks.onRemoteStream(stream);
      }
    });

    // Handle connection
    this.peer.on('connect', () => {
      console.log('Peer connection established');
      if (this.callbacks.onConnected) {
        this.callbacks.onConnected();
      }
    });

    // Handle errors
    this.peer.on('error', (error) => {
      console.error('Peer error:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    });

    // Handle close
    this.peer.on('close', () => {
      console.log('Peer connection closed');
      if (this.callbacks.onClose) {
        this.callbacks.onClose();
      }
      this.cleanup();
    });
  }

  // Handle incoming signaling data
  handleSignal(signalData) {
    if (!this.peer) {
      // Create peer as responder if not exists
      this.createPeer(false);
    }

    try {
      this.peer.signal(signalData);
    } catch (error) {
      console.error('Error handling signal:', error);
    }
  }

  // Toggle video
  toggleVideo(enabled) {
    if (this.localStream) {
      const videoTrack = this.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = enabled;
      }
    }
  }

  // Toggle audio
  toggleAudio(enabled) {
    if (this.localStream) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = enabled;
      }
    }
  }

  // End the call
  async endCall() {
    try {
      if (this.callId && this.remoteUserId) {
        await chatService.sendSignal(
          this.callId,
          'call-ended',
          {},
          this.remoteUserId
        );
      }
    } catch (error) {
      console.error('Error ending call:', error);
    } finally {
      this.cleanup();
    }
  }

  // Reject incoming call
  async rejectCall() {
    try {
      if (this.callId && this.remoteUserId) {
        await chatService.sendSignal(
          this.callId,
          'call-rejected',
          {},
          this.remoteUserId
        );
      }
    } catch (error) {
      console.error('Error rejecting call:', error);
    } finally {
      this.cleanup();
    }
  }

  // Cleanup resources
  cleanup() {
    this.stopLocalStream();
    
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }

    this.remoteStream = null;
    this.callId = null;
    this.conversationId = null;
    this.remoteUserId = null;
    this.isInitiator = false;
  }
}

// Singleton instance
const webRTCService = new WebRTCService();
export default webRTCService;
